import React from "react";

const dayLabels = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

// ===============================
// Inline SVG – zelený check v kruhu
// ===============================
function CoveredDayIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-label="Den plně pokrytý"
    >
      <circle cx="12" cy="12" r="12" fill="#16a34a" /> {/* green-600 */}
      <path
        d="M7 12.5l3 3 7-7"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Lokální Date → YYYY-MM-DD (bez UTC posunu)
function formatISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function MonthCalendar({
  year,
  month, // 1–12
  onWeekSelect,
  onMonthChange,
  selectedWeekDates = [],
  coverageByDate = {}, // { "YYYY-MM-DD": "full" }
}) {
  const jsMonth = month - 1;
  const firstOfMonth = new Date(year, jsMonth, 1);
  const lastOfMonth = new Date(year, jsMonth + 1, 0);
  const daysInMonth = lastOfMonth.getDate();

  // JS: 0=Ne … chceme Po=0
  const jsDay = firstOfMonth.getDay();
  const startOffset = jsDay === 0 ? 6 : jsDay - 1;

  const grid = [];
  const totalCells = startOffset + daysInMonth;
  const gridSize = Math.ceil(totalCells / 7) * 7;

  for (let i = 0; i < gridSize; i++) {
    const dayNum = i - startOffset + 1;
    const date = new Date(year, jsMonth, dayNum);

    grid.push({
      iso: formatISO(date),
      dayNum: date.getDate(),
      isCurrentMonth: dayNum >= 1 && dayNum <= daysInMonth,
    });
  }

  const weeks = [];
  for (let i = 0; i < grid.length; i += 7) {
    weeks.push(grid.slice(i, i + 7));
  }

  const selectedSet = new Set(selectedWeekDates);

  const handleDayClick = (cell) => {
    if (!cell?.iso) return;
    onWeekSelect(cell.iso);
  };

  const handlePrevMonth = () => {
    let newMonth = month - 1;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    onMonthChange(newYear, newMonth);
  };

  const handleNextMonth = () => {
    let newMonth = month + 1;
    let newYear = year;
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }
    onMonthChange(newYear, newMonth);
  };

  const monthLabel = firstOfMonth.toLocaleDateString("cs-CZ", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="border rounded-xl bg-white shadow-sm p-4 mb-4">
      {/* Hlavička */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="px-2 py-1 text-sm rounded hover:bg-gray-100"
        >
          ←
        </button>
        <div className="font-semibold text-gray-800 text-sm uppercase">
          {monthLabel}
        </div>
        <button
          type="button"
          onClick={handleNextMonth}
          className="px-2 py-1 text-sm rounded hover:bg-gray-100"
        >
          →
        </button>
      </div>

      {/* Hlavička dnů */}
      <div className="grid grid-cols-7 text-xs text-gray-500 mb-1">
        {dayLabels.map((d) => (
          <div key={d} className="text-center py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Dny */}
      <div className="grid grid-cols-7 gap-[2px] text-sm">
        {weeks.map((week, wIndex) =>
          week.map((cell, dIndex) => {
            const isSelected = selectedSet.has(cell.iso);
            const isCovered = coverageByDate?.[cell.iso] === "full";

            return (
              <button
                key={`${wIndex}-${dIndex}`}
                onClick={() => handleDayClick(cell)}
                className={`
                  h-7 w-full rounded flex items-center justify-center transition
                  ${cell.isCurrentMonth ? "text-gray-800" : "text-gray-400"}
                  ${
                    isSelected
                      ? "bg-blue-600 text-white font-semibold"
                      : "hover:bg-blue-100"
                  }
                `}
                title={isCovered ? "Den je plně pokrytý" : ""}
              >
                {isCovered ? (
                  <CoveredDayIcon />
                ) : (
                  cell.dayNum
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="mt-2 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <CoveredDayIcon size={14} />
          = den plně pokrytý (magistři + asistenti)
        </span>
      </div>
    </div>
  );
}
